import {
  animate,
  ImageScaleViewOpts,
  MovieClip,
  View,
} from '@play-co/timestep-core/ui';
import { Match3 } from 'src/replicant/minigame/match3';
import { DragEvent } from '@play-co/timestep-core/lib/event/input/InputHandler';
import InputEvent from '@play-co/timestep-core/lib/event/input/InputEvent';
import { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import { animDuration } from 'src/lib/utils';

class AnimationConfig {
  constructor(
    public url: string,
    public animations: {
      remove: string;
    },
  ) {}
}

export const animationConfig: Record<Match3.BlockType, AnimationConfig> = {
  c: new AnimationConfig('assets/minigame/match3/crab/animations', {
    remove: 'linepet_crab_tile',
  }),
  d: new AnimationConfig('assets/minigame/match3/dog/animations', {
    remove: 'linepet_dog_tile',
  }),
  f: new AnimationConfig('assets/minigame/match3/fish/animations', {
    remove: 'linepet_fish_tile',
  }),
  g: new AnimationConfig('assets/minigame/match3/gem/animations', {
    remove: 'linepet_gem_tile',
  }),
  p: new AnimationConfig('assets/minigame/match3/pig/animations', {
    remove: 'linepet_pig_tile',
  }),
  t: new AnimationConfig('assets/minigame/match3/turtle/animations', {
    remove: 'linepet_turtle_tile',
  }),
};

export default class Match3Block {
  readonly view: View;
  private blockClip: MovieClip;
  private offsetStart: Pick<ViewOpts, 'offsetX' | 'offsetY'> | null = null;
  private dragCancelled = false;

  constructor(
    public readonly type: Match3.BlockType,
    private position: Match3.BoardPosition,
    private isDraggingEnabled: () => boolean,
    opts: Omit<ImageScaleViewOpts, 'image' | 'scaleMethod'>,
  ) {
    this.view = Object.assign(new View(opts), {
      onInputStart: (e) => this.onInputStart(e),
      onDragStart: () => this.onDragStart(),
      onDragStop: () => this.onDragStop(),
      onDrag: (e) => this.onDrag(e),
    });

    const { width, height } = this.view.style;
    this.blockClip = this.view.addSubview(
      new MovieClip({
        x: width / 2,
        y: height / 2,
        width: width - 2 * skin.block.padding,
        height: height - 2 * skin.block.padding,
        url: animationConfig[type].url,
      }),
    );

    const { animations } = animationConfig[type];
    this.blockClip.play(animations.remove);
    this.blockClip.pause();
  }

  playAnimation(animation: 'remove', duration?: number) {
    const animationName = animationConfig[this.type].animations[animation];
    if (duration) {
      const animDuration = this.blockClip.getAnimationDuration(animationName);
      this.blockClip.speed = animDuration / duration;
    }
    this.blockClip.play(animationName);
  }

  isDragging = () => !this.dragCancelled && this.view.isDragging();

  cancelDrag = async () => {
    this.dragCancelled = true;
    await this.drop();
  };

  drop = async () => {
    await this.moveToPoint(this.view.style);
    this.view.style.zIndex = 0;
    this.offsetStart = null;
  };

  private onInputStart = (e: InputEvent) => {
    if (this.isDraggingEnabled()) {
      this.view.startDrag({ radius: 10, inputStartEvent: e });
      e.cancel();
    }
  };

  private onDragStart = () => {
    this.dragCancelled = false;
    this.view.style.zIndex = 1;
    const { offsetX, offsetY } = this.view.style;
    this.offsetStart = { offsetX, offsetY };
    animate(this.view).now({ opacity: 0.7 }, 100);
  };

  private onDragStop = () => {
    if (!this.dragCancelled) {
      void this.drop();
    }
  };

  private onDrag = (e: DragEvent) => {
    if (!this.isDragging()) {
      return;
    }

    const { currPt, srcPoint } = e;
    const deltaX = currPt.x - srcPoint.x;
    const deltaY = currPt.y - srcPoint.y;

    let { offsetX, offsetY } = this.offsetStart!;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      offsetX += deltaX;
    } else {
      offsetY += deltaY;
    }

    this.view.updateOpts({ offsetX, offsetY });
  };

  moveToPoint = (
    { x, y }: Pick<ViewOpts, 'x' | 'y'>,
    duration = animDuration,
  ) => {
    return animate(this.view)
      .now(
        { ...this.offsetStart, x, y, opacity: 1 },
        duration,
        animate.easeInOut,
      )
      .toPromise();
  };

  getBoardPosition = (): Match3.BoardPosition => ({ ...this.position });

  setBoardPosition = (position: Match3.BoardPosition) => {
    this.position = { ...position };
  };
}

const skin = {
  block: {
    padding: 4,
  },
};
